import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../context";
import useAdd from "../../../../api/useAdd";

const AddAsset = ({ open, onClose }) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  const { handleAdd, resetForm, loading } = useAdd("general-stock", token);

  const [formData, setFormData] = useState({
    name: "",
    quantity: "",
    purchase_date: "",
    cost_per_unit: "",
    location: "",
    description: "",
    image: null,
  });

  const [imagePreview, setImagePreview] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (resetForm) {
      setFormData({
        name: "",
        quantity: "",
        purchase_date: "",
        cost_per_unit: "",
        location: "",
        description: "",
        image: null,
      });
      setImagePreview(null); // Reset image preview
      onClose();
    }
  }, [resetForm]);

  const validate = () => {
    let tempErrors = {};
    if (!formData.name) tempErrors.name = t("hr.nameRequired");
    if (!formData.quantity || formData.quantity <= 0)
      tempErrors.quantity = t("hr.quantityRequired");
    if (!formData.purchase_date)
      tempErrors.purchase_date = t("hr.purchaseDateRequired");
    if (!formData.cost_per_unit || formData.cost_per_unit <= 0)
      tempErrors.cost_per_unit = t("hr.costPerUnitRequired");
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;

    if (files && files[0]) {
      const file = files[0];

      setErrors({ ...errors, [name]: null });
      setFormData({ ...formData, [name]: file });

      const reader = new FileReader();
      reader.onload = (event) => {
        setImagePreview(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (validate()) {
      const data = new FormData();
      Object.keys(formData).forEach((key) => {
        if (formData[key] !== null && formData[key] !== "") {
          data.append(key, formData[key]);
        }
      });
      handleAdd(data);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md">
      <DialogContent>
        <Box sx={{ paddingY: 4, maxWidth: "600px", marginX: "auto" }}>
          <Typography variant="h5" fontWeight={600} align="center" gutterBottom>
            {t("hr.addAsset")}
          </Typography>

          <Box sx={{ marginTop: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t("hr.name")}
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  margin="dense"
                  error={!!errors.name}
                  helperText={errors.name}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t("hr.quantity")}
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleChange}
                  type="number"
                  variant="outlined"
                  fullWidth
                  margin="dense"
                  error={!!errors.quantity}
                  helperText={errors.quantity}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t("hr.purchaseDate")}
                  name="purchase_date"
                  value={formData.purchase_date}
                  onChange={handleChange}
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                  fullWidth
                  margin="dense"
                  error={!!errors.purchase_date}
                  helperText={errors.purchase_date}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t("hr.costPerUnit")}
                  name="cost_per_unit"
                  value={formData.cost_per_unit}
                  onChange={handleChange}
                  type="number"
                  variant="outlined"
                  fullWidth
                  margin="dense"
                  error={!!errors.cost_per_unit}
                  helperText={errors.cost_per_unit}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t("hr.location")}
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  margin="dense"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t("hr.description")}
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  multiline
                  rows={3}
                  variant="outlined"
                  fullWidth
                  margin="dense"
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  margin="dense"
                >
                  {t("hr.uploadImage")}
                  <input
                    type="file"
                    name="image"
                    hidden
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                </Button>
                {formData.image && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {formData.image.name}
                  </Typography>
                )}
                {errors.image && (
                  <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                    {errors.image}
                  </Typography>
                )}
                {/* Display image preview */}
                {imagePreview && (
                  <Box
                    sx={{
                      marginTop: 2,
                      textAlign: "center",
                    }}
                  >
                    <img
                      src={imagePreview}
                      alt="Preview"
                      style={{
                        maxWidth: "15vw",
                        maxHeight: "13vh",
                        borderRadius: "8px",
                      }}
                    />
                  </Box>
                )}
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "center", marginTop: 4 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              startIcon={loading && <CircularProgress size={20} />}
              disabled={loading}
            >
              {t("hr.save")}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AddAsset;
